import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Card,
    CardContent,
    Fade,
    useTheme,
    Switch,
    FormControlLabel,
    Chip
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Settings as SettingsIcon,
    Class as ClassIcon,
    Category as CategoryIcon,
    Group as GroupIcon,
    Apartment as ApartmentIcon,
    CalendarMonth as CalendarIcon,
    CheckCircle,
    Email as EmailIcon
} from '@mui/icons-material';
import { masterDataAPI, academicYearAPI, schoolSettingsAPI } from '../services/api';
import { toast } from 'react-toastify';

const Settings = () => {
    const theme = useTheme();
    const [openDialog, setOpenDialog] = useState(false);
    const [currentType, setCurrentType] = useState('ACADEMIC_YEAR');
    const [dataList, setDataList] = useState([]);
    const [formData, setFormData] = useState({ id: null, name: '', code: '' });

    const [academicYearForm, setAcademicYearForm] = useState({
        id: null,
        name: '',
        startDate: '',
        endDate: '',
        isCurrent: false
    });

    // SMTP Config
    const [smtpConfig, setSmtpConfig] = useState({
        SMTP_HOST: '',
        SMTP_PORT: '',
        SMTP_USER: '',
        SMTP_PASS: '',
        SMTP_FROM: ''
    });

    const settingTypes = [
        { label: 'Academic Year', value: 'ACADEMIC_YEAR', icon: <CalendarIcon /> },
        { label: 'Religion', value: 'RELIGION', icon: <GroupIcon /> },
        { label: 'Caste', value: 'CASTE', icon: <GroupIcon /> },
        { label: 'Category', value: 'CATEGORY', icon: <CategoryIcon /> },
        { label: 'Standard', value: 'STANDARD', icon: <ClassIcon /> },
        { label: 'Division', value: 'DIVISION', icon: <ApartmentIcon /> },
        { label: 'Email Config', value: 'SMTP_CONFIG', icon: <EmailIcon /> }
    ];

    useEffect(() => {
        if (currentType) {
            fetchData();
        }
    }, [currentType]);

    const fetchData = async () => {
        try {
            if (currentType === 'ACADEMIC_YEAR') {
                const res = await academicYearAPI.getAll();
                setDataList(res.data.data || []);
            } else if (currentType === 'SMTP_CONFIG') {
                const res = await schoolSettingsAPI.getAll();
                const settings = res.data.data || {};
                setSmtpConfig({
                    SMTP_HOST: settings.SMTP_HOST || '',
                    SMTP_PORT: settings.SMTP_PORT || '',
                    SMTP_USER: settings.SMTP_USER || '',
                    SMTP_PASS: settings.SMTP_PASS || '',
                    SMTP_FROM: settings.SMTP_FROM || ''
                });
            } else {
                const res = await masterDataAPI.getAll({ type: currentType });
                setDataList(res.data.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch data");
        }
    };

    const handleOpen = (type) => {
        setCurrentType(type);
    };

    const handleSave = async () => {
        try {
            if (currentType === 'ACADEMIC_YEAR') {
                if (academicYearForm.id) {
                    await academicYearAPI.update(academicYearForm.id, academicYearForm);
                } else {
                    await academicYearAPI.create(academicYearForm);
                }
            } else if (currentType === 'SMTP_CONFIG') {
                await schoolSettingsAPI.bulkUpdate({ settings: smtpConfig });
            } else {
                const payload = { ...formData, type: currentType };
                if (formData.id) {
                    await masterDataAPI.update(formData.id, payload);
                } else {
                    await masterDataAPI.create(payload);
                }
            }
            toast.success("Saved Successfully");
            resetForms();
            fetchData();
        } catch (error) {
            toast.error("Operation Failed");
        }
    };

    const resetForms = () => {
        setFormData({ id: null, name: '', code: '' });
        setAcademicYearForm({ id: null, name: '', startDate: '', endDate: '', isCurrent: false });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            if (currentType === 'ACADEMIC_YEAR') {
                await academicYearAPI.delete(id);
            } else {
                await masterDataAPI.delete(id);
            }
            toast.success("Deleted Successfully");
            fetchData();
        } catch (error) {
            toast.error("Delete Failed");
        }
    };

    const handleSetCurrentYear = async (year) => {
        try {
            await academicYearAPI.update(year.id, { ...year, isCurrent: true });
            toast.success(`${year.name} set as current year`);
            fetchData();
        } catch (error) {
            toast.error("Failed to update");
        }
    };

    const handleEditAcademicYear = (item) => {
        setAcademicYearForm({
            id: item.id,
            name: item.name,
            startDate: item.startDate?.split('T')[0] || '',
            endDate: item.endDate?.split('T')[0] || '',
            isCurrent: item.isCurrent || false
        });
        setOpenDialog(true);
    };

    const renderAcademicYears = () => (
        <Grid container spacing={3}>
            {dataList.map(item => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            border: item.isCurrent ? '2px solid' : '1px solid #eee',
                            borderColor: item.isCurrent ? 'primary.main' : '#eee',
                            transition: 'all 0.2s',
                            bgcolor: item.isCurrent ? 'primary.light' : 'white',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
                            }
                        }}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: item.isCurrent ? 'white' : 'inherit' }}>
                                            {item.name}
                                        </Typography>
                                        {item.isCurrent && (
                                            <Chip
                                                label="Current"
                                                size="small"
                                                icon={<CheckCircle />}
                                                sx={{ bgcolor: 'white', color: 'primary.main' }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 1, color: item.isCurrent ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                                        {item.startDate} to {item.endDate}
                                    </Typography>
                                </Box>
                                <Box>
                                    <IconButton size="small" onClick={() => handleEditAcademicYear(item)} sx={{ color: item.isCurrent ? 'white' : 'inherit' }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(item.id)} disabled={item.isCurrent}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                            {!item.isCurrent && (
                                <Button
                                    size="small"
                                    sx={{ mt: 2 }}
                                    onClick={() => handleSetCurrentYear(item)}
                                >
                                    Set as Current
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            ))}
            {dataList.length === 0 && (
                <Grid item xs={12}>
                    <Box sx={{
                        p: 8,
                        textAlign: 'center',
                        border: '2px dashed #e0e0e0',
                        borderRadius: 4,
                        color: 'text.secondary'
                    }}>
                        <Typography>No academic years found. Create your first one!</Typography>
                        <Button sx={{ mt: 2 }} onClick={() => setOpenDialog(true)}>Add Academic Year</Button>
                    </Box>
                </Grid>
            )}
        </Grid>
    );

    const renderMasterData = () => (
        <Grid container spacing={3}>
            {dataList.map(item => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            border: '1px solid #eee',
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: 'primary.main',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
                            }
                        }}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{item.name}</Typography>
                                    {item.code && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, bgcolor: 'grey.100', display: 'inline-block', px: 1, borderRadius: 1 }}>{item.code}</Typography>}
                                </Box>
                                <Box>
                                    <IconButton size="small" onClick={() => { setFormData(item); setOpenDialog(true); }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
            {dataList.length === 0 && (
                <Grid item xs={12}>
                    <Box sx={{
                        p: 8,
                        textAlign: 'center',
                        border: '2px dashed #e0e0e0',
                        borderRadius: 4,
                        color: 'text.secondary'
                    }}>
                        <Typography>No records found in {settingTypes.find(t => t.value === currentType)?.label}.</Typography>
                        <Button sx={{ mt: 2 }} onClick={() => setOpenDialog(true)}>Create First Record</Button>
                    </Box>
                </Grid>
            )}
        </Grid>
    );

    const renderSMTPConfig = () => (
        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee', maxWidth: 600 }}>
            <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        Configure the SMTP details used for sending generic emails and password reset links.
                    </Typography>
                    <TextField
                        label="SMTP Host (e.g. smtp.gmail.com)"
                        fullWidth value={smtpConfig.SMTP_HOST}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_HOST: e.target.value })}
                    />
                    <TextField
                        label="SMTP Port (e.g. 587 or 465)"
                        fullWidth value={smtpConfig.SMTP_PORT}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_PORT: e.target.value })}
                    />
                    <TextField
                        label="SMTP Username/Email"
                        fullWidth value={smtpConfig.SMTP_USER}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_USER: e.target.value })}
                    />
                    <TextField
                        label="SMTP Password / App Password"
                        type="password" fullWidth value={smtpConfig.SMTP_PASS}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_PASS: e.target.value })}
                    />
                    <TextField
                        label="From Email Address (Sender)"
                        fullWidth value={smtpConfig.SMTP_FROM}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, SMTP_FROM: e.target.value })}
                        helperText="Defaults to noreply@vpschool.com if empty"
                    />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={handleSave} sx={{ px: 4 }}>
                            Save Configuration
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );


    return (
        <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: 'primary.light', color: 'white', display: 'flex' }}>
                    <SettingsIcon fontSize="large" />
                </Box>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Settings</Typography>
                    <Typography color="text.secondary">Manage academic years and master data</Typography>
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ height: 'calc(100vh - 180px)' }}>
                {/* Sidebar */}
                <Grid item xs={12} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            height: '100%',
                            borderRadius: 4,
                            bgcolor: 'white',
                            border: '1px solid #eee',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ p: 2 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Configuration
                            </Typography>
                        </Box>
                        {settingTypes.map(type => (
                            <Box
                                key={type.value}
                                onClick={() => handleOpen(type.value)}
                                sx={{
                                    p: 2,
                                    mx: 1,
                                    mb: 1,
                                    borderRadius: 3,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    transition: 'all 0.2s',
                                    bgcolor: currentType === type.value ? 'primary.main' : 'transparent',
                                    color: currentType === type.value ? 'white' : 'text.primary',
                                    '&:hover': {
                                        bgcolor: currentType === type.value ? 'primary.dark' : 'grey.100',
                                        transform: 'translateX(4px)'
                                    }
                                }}
                            >
                                {React.cloneElement(type.icon, { color: currentType === type.value ? 'inherit' : 'action' })}
                                <Typography sx={{ fontWeight: 500 }}>{type.label}</Typography>
                            </Box>
                        ))}
                    </Paper>
                </Grid>

                {/* Main Content */}
                <Grid item xs={12} md={9}>
                    <Fade in={true} key={currentType}>
                        <Box sx={{ height: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {settingTypes.find(t => t.value === currentType)?.label} Management
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="contained"
                                    onClick={() => { resetForms(); setOpenDialog(true); }}
                                    sx={{ borderRadius: 3, px: 3, py: 1, boxShadow: theme.shadows[4] }}
                                >
                                    Add New
                                </Button>
                            </Box>

                            {currentType === 'ACADEMIC_YEAR' 
                                ? renderAcademicYears() 
                                : currentType === 'SMTP_CONFIG' 
                                    ? renderSMTPConfig() 
                                    : renderMasterData()}
                        </Box>
                    </Fade>
                </Grid>
            </Grid>

            {/* Dialog for Master Data */}
            <Dialog
                open={openDialog && currentType !== 'ACADEMIC_YEAR'}
                onClose={() => setOpenDialog(false)}
                PaperProps={{ sx: { borderRadius: 4, width: 400 } }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
                    {formData.id ? 'Edit' : 'Add'} Record
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            label="Name"
                            fullWidth
                            variant="outlined"
                            autoFocus
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <TextField
                            label="Code (Optional)"
                            fullWidth
                            variant="outlined"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDialog(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => { handleSave(); setOpenDialog(false); }}
                        sx={{ borderRadius: 2, px: 3, boxShadow: 'none' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for Academic Year */}
            <Dialog
                open={openDialog && currentType === 'ACADEMIC_YEAR'}
                onClose={() => setOpenDialog(false)}
                PaperProps={{ sx: { borderRadius: 4, width: 500 } }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
                    {academicYearForm.id ? 'Edit' : 'Add'} Academic Year
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            label="Academic Year Name"
                            placeholder="e.g., 2026-2027"
                            fullWidth
                            variant="outlined"
                            autoFocus
                            value={academicYearForm.name}
                            onChange={(e) => setAcademicYearForm({ ...academicYearForm, name: e.target.value })}
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={academicYearForm.startDate}
                                    onChange={(e) => setAcademicYearForm({ ...academicYearForm, startDate: e.target.value })}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="End Date"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={academicYearForm.endDate}
                                    onChange={(e) => setAcademicYearForm({ ...academicYearForm, endDate: e.target.value })}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                            </Grid>
                        </Grid>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={academicYearForm.isCurrent}
                                    onChange={(e) => setAcademicYearForm({ ...academicYearForm, isCurrent: e.target.checked })}
                                />
                            }
                            label="Set as Current Academic Year"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDialog(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => { handleSave(); setOpenDialog(false); }}
                        sx={{ borderRadius: 2, px: 3, boxShadow: 'none' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Settings;
